/**
 * ╔══════════════════════════════════════════════════════════════════╗
 * ║         NSL · FINANCIAL & CRM HUB — CONTENEDOR PRINCIPAL        ║
 * ║         FinancialDashboard.jsx  ·  v1.0.0                       ║
 * ║         Arquitecto: NextSpeak Lab © 2025                        ║
 * ╚══════════════════════════════════════════════════════════════════╝
 *
 * Este componente es el "quirófano central" del Financial Hub.
 * Gestiona la navegación de 16 pestañas y el lazy-render de cada módulo.
 *
 * ESTRUCTURA DE CARPETA ESPERADA:
 *   src/components/admin/financial_hub/
 *     ├── FinancialDashboard.jsx   ← este archivo
 *     ├── FinancialDashboard.css
 *     └── tabs/
 *         ├── OverviewTab.jsx
 *         ├── FinanzasTab.jsx
 *         ├── AlumnosTab.jsx
 *         ├── TeachersTab.jsx
 *         ├── B2BTab.jsx
 *         ├── PlanesTab.jsx
 *         ├── TokensTab.jsx
 *         ├── AlertasTab.jsx
 *         ├── PasarelasTab.jsx
 *         ├── FreemiumTab.jsx
 *         ├── CalendarTab.jsx
 *         ├── NominaTab.jsx
 *         ├── CommsTab.jsx
 *         ├── ReportesTab.jsx
 *         ├── ChurnTab.jsx
 *         └── RatingsTab.jsx
 */

import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  Suspense,
  lazy,
} from 'react';
import './Financialdashboard.css';
import apiClient from '../../../../../api/apiClient';
import { useLatido } from '../../../../../hooks/useLatido';

// ─────────────────────────────────────────────
// 🔬 LAZY IMPORTS — Carga diferida de cada pestaña.
// Cuando la pestaña no exista aún, Suspense mostrará el Quirófano Loader.
// ─────────────────────────────────────────────
const FreemiumTab = lazy(() => import('./Freemiumtab/Freemiumtab'));
const AlumnosTab = lazy(() => import('./Alumnostab/Alumnostab'));
const FinanzasTab = lazy(() => import('./Finanzastab/Finanzastab'));
const TeachersTab = lazy(() => import('./TeachersTab/TeachersTab'));
// La pestaña B2B antigua queda OCULTA, no borrada (EMPRESAS.md D10, T7.3): la
// sustituye «Empresas». Sus archivos siguen en ./B2BTab por si hay que consultarla.
// const B2BTab = lazy(() => import('./B2BTab/B2BTab'));
const EmpresasTab = lazy(() => import('./EmpresasTab/EmpresasTab'));
const PlanesTab = lazy(() => import('./PlanesTab/PlanesTab'));
const TokensTab = lazy(() => import('./TokensTab/TokensTab'));
const AlertasTab = lazy(() => import('./AlertasTab/AlertasTab'));
const PasarelasTab = lazy(() => import('./PasarelasTab/PasarelasTab'));
const CalendarTab = lazy(() => import('./CalendarTab/CalendarTab'));
const NominaTab = lazy(() => import('./NominaTab/NominaTab'));
const CommsTab = lazy(() => import('./CommsTab/CommsTab'));
const ReportesTab = lazy(() => import('./ReportesTab/ReportesTab'));
const ChurnTab = lazy(() => import('./ChurnTab/ChurnTab'));
const RatingsTab = lazy(() => import('./RatingsTab/RatingsTab'));
const LiveChatTabV2 = lazy(() => import('./LiveChatTabV2/LiveChatTabV2'));

// 🔥 COMENTA TODAS LAS DEMÁS TEMPORALMENTE (Ponle // al inicio de la línea)
// const FinanzasTab  = lazy(() => import('./tabs/FinanzasTab'));
// const AlumnosTab   = lazy(() => import('./tabs/AlumnosTab'));
// const TeachersTab  = lazy(() => import('./tabs/TeachersTab'));
// const B2BTab       = lazy(() => import('./tabs/B2BTab'));
// const PlanesTab    = lazy(() => import('./tabs/PlanesTab'));
// const TokensTab    = lazy(() => import('./tabs/TokensTab'));
// const AlertasTab   = lazy(() => import('./tabs/AlertasTab'));
// const PasarelasTab = lazy(() => import('./tabs/PasarelasTab'));
// const FreemiumTab  = lazy(() => import('./tabs/FreemiumTab'));
// const CalendarTab  = lazy(() => import('./tabs/CalendarTab'));
// const NominaTab    = lazy(() => import('./tabs/NominaTab'));
// const CommsTab     = lazy(() => import('./tabs/CommsTab'));
// const ReportesTab  = lazy(() => import('./tabs/ReportesTab'));
// const ChurnTab     = lazy(() => import('./tabs/ChurnTab'));
// const RatingsTab   = lazy(() => import('./tabs/RatingsTab'));

// ─────────────────────────────────────────────
// 🚧 ÓRGANO DE PLÁSTICO PARA EVITAR ERRORES
// ─────────────────────────────────────────────
const PlaceholderTab = () => (
  <div style={{ padding: '60px 20px', textAlign: 'center' }}>
    <div style={{ fontSize: '40px' }}>🚧</div>
    <h2 style={{ color: 'var(--tx)' }}>Módulo en Construcción</h2>
  </div>
);

// ─────────────────────────────────────────────
// 📋 DEFINICIÓN DE PESTAÑAS
// Fuente de verdad para menú y router interno.
// ─────────────────────────────────────────────
const TABS = [
  { id: 'chat-v2', icon: '💬', label: 'Live Chat', component: LiveChatTabV2, description: 'Chat en vivo con alumnos' },
  { id: 'finanzas', icon: '💰', label: 'Finanzas', component: FinanzasTab, badge: null, accentVar: '--ok', description: 'Ingresos, egresos y flujo de caja' },
  { id: 'alumnos', icon: '🎓', label: 'CRM Alumnos', component: AlumnosTab, badge: null, accentVar: '--pur', description: 'Gestión de estudiantes individuales' },
  { id: 'teachers', icon: '👩‍🏫', label: 'Teachers', component: TeachersTab, badge: null, accentVar: '--pnk', description: 'Docentes, horarios y rendimiento' },
  { id: 'empresas', icon: '🏭', label: 'Empresas', component: EmpresasTab, badge: null, accentVar: '--ylw', description: 'Empresas, contratos, grupos, empleados y acceso a su portal' },
  // { id: 'b2b', icon: '🏢', label: 'B2B', component: B2BTab, badge: null, accentVar: '--blu', description: 'Clientes corporativos y contratos' },
  { id: 'planes', icon: '📦', label: 'Planes', component: PlanesTab, badge: null, accentVar: '--ylw', description: 'Planes de suscripción y precios' },
  { id: 'tokens', icon: '🤖', label: 'IA Tokens', component: TokensTab, badge: null, accentVar: '--ac', description: 'Consumo y gestión de tokens IA' },
  { id: 'alertas', icon: '🔔', label: 'Alertas', component: AlertasTab, badge: null, accentVar: '--er', description: 'Alertas críticas y notificaciones' },
  { id: 'pasarelas', icon: '💳', label: 'Pasarelas', component: PasarelasTab, badge: null, accentVar: '--ok', description: 'Cómo te pagan tus alumnos' },
  { id: 'freemium', icon: '🆓', label: 'Freemium', component: FreemiumTab, badge: null, accentVar: '--pur', description: 'Conversión y embudo freemium' },
  { id: 'calendario', icon: '📅', label: 'Calendario', component: CalendarTab, badge: null, accentVar: '--ac', description: 'Clases, pagos y eventos del mes' },
  { id: 'nomina', icon: '💼', label: 'Nómina', component: NominaTab, badge: null, accentVar: '--ok', description: 'Pagos y comisiones a docentes' },
  { id: 'comms', icon: '📨', label: 'Comms', component: CommsTab, badge: null, accentVar: '--pnk', description: 'Comunicaciones y recordatorios' },
  { id: 'reportes', icon: '📊', label: 'Reportes', component: ReportesTab, badge: null, accentVar: '--blu', description: 'Reportes y exportaciones' },
  { id: 'churn', icon: '📉', label: 'Churn', component: ChurnTab, badge: null, accentVar: '--er', description: 'Riesgo de cancelación y retención' },
  { id: 'ratings', icon: '⭐', label: 'Ratings', component: RatingsTab, badge: null, accentVar: '--ylw', description: 'Satisfacción y calificaciones' },
];

// ─────────────────────────────────────────────
// 🏥 QUIRÓFANO LOADER
// Se muestra mientras se carga una pestaña lazy
// o cuando el módulo no existe aún.
// ─────────────────────────────────────────────
const QuirofanoLoader = ({ tabLabel = '' }) => (
  <div className="nsl-quirofano">
    <div className="nsl-qrf-inner">
      {/* Ícono animado */}
      <div className="nsl-qrf-pulse">
        <span className="nsl-qrf-icon">⚕️</span>
        <div className="nsl-qrf-ring nsl-qrf-ring--1" />
        <div className="nsl-qrf-ring nsl-qrf-ring--2" />
        <div className="nsl-qrf-ring nsl-qrf-ring--3" />
      </div>

      {/* Texto */}
      <p className="nsl-qrf-title">Quirófano Preparando…</p>
      <p className="nsl-qrf-sub">
        {tabLabel
          ? `Ensamblando módulo "${tabLabel}"`
          : 'Cargando módulo del sistema'}
      </p>

      {/* Barra de progreso simulada */}
      <div className="nsl-qrf-bar-track">
        <div className="nsl-qrf-bar-fill" />
      </div>

      {/* Monograma NSL */}
      <span className="nsl-qrf-badge">NSL · FINANCIAL HUB</span>
    </div>
  </div>
);

// ─────────────────────────────────────────────
// 🧪 ERROR BOUNDARY para tabs que fallen al importar
// ─────────────────────────────────────────────
class TabErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, errorMsg: '' };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, errorMsg: error?.message || 'Error desconocido' };
  }

  componentDidCatch(error, info) {
    console.warn(`[NSL Hub] Error en pestaña "${this.props.tabLabel}":`, error, info);
  }

  render() {
    if (this.state.hasError) {
      // Antes aquí se enseñaba el «Quirófano preparando…», el mismo cartel que
      // cuando la pestaña todavía está cargando: una pestaña ROTA parecía una
      // pestaña lenta y nadie iba a mirar la consola (22-09-2026).
      return (
        <div data-prueba="hub-error" style={{ padding: "48px 24px", textAlign: "center", color: "var(--tx2)" }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>⚠️</div>
          <div style={{ fontWeight: 700, color: "#ff5e7e", marginBottom: 8 }}>
            La pestaña «{this.props.tabLabel}» no se pudo mostrar
          </div>
          <div style={{ fontSize: 12, marginBottom: 18 }}>{this.state.errorMsg}</div>
          <button onClick={() => window.location.reload()} style={{ padding: "8px 20px", borderRadius: 10, border: "1px solid rgba(255,94,126,.4)", background: "transparent", color: "#ff5e7e", cursor: "pointer", fontFamily: "inherit", fontSize: 12 }}>
            Reintentar
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// ─────────────────────────────────────────────
// 🔝 TOPBAR — Barra superior con logo NSL
// ─────────────────────────────────────────────
const Topbar = ({ theme }) => {
  // Fecha localizada
  const [dateStr, setDateStr] = useState('');

  useEffect(() => {
    const d = new Date();
    setDateStr(
      d.toLocaleDateString('es-MX', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    );
  }, []);

  return (
    <header className="nsl-topbar">
      {/* Logo */}
      <div className="nsl-logo-wrap">
        <div className="nsl-logo-mark">NSL</div>
        <div>
          <div className="nsl-logo-name">NextSpeak Lab</div>
          <div className="nsl-logo-sub">Financial &amp; CRM Hub</div>
        </div>
      </div>

      {/* Fecha */}
      <div className="nsl-topbar-date">{dateStr}</div>

      {/* Controles derechos */}
      <div className="nsl-topbar-right">
        {/* Indicador live */}
        <div className="nsl-live-badge">
          <span className="nsl-live-dot" />
          <span className="nsl-live-label">LIVE</span>
        </div>

        {/* 🔥 EL BOTÓN DE TEMA FUE EXTIRPADO 🔥 */}

        {/* Avatar de usuario */}
        <div className="nsl-user-avatar" title="Administrador NSL">
          AD
        </div>
      </div>
    </header>
  );
};

// ─────────────────────────────────────────────
// 🧭 NAVBAR — Barra de 16 pestañas horizontales
// ─────────────────────────────────────────────
const Navbar = ({ activeTab, onTabChange, badges = {} }) => (
  <nav className="nsl-navbar" role="tablist" aria-label="Módulos del Hub">
    {TABS.map((tab) => (
      <button
        key={tab.id}
        role="tab"
        aria-selected={activeTab === tab.id}
        aria-controls={`nsl-panel-${tab.id}`}
        id={`nsl-tab-${tab.id}`}
        className={`nsl-nav-tab${activeTab === tab.id ? ' nsl-nav-tab--active' : ''}`}
        onClick={() => onTabChange(tab.id)}
        title={tab.description}
      >
        {/* Ícono */}
        <span className="nsl-tab-icon" aria-hidden="true">
          {tab.icon}
        </span>

        {/* Label */}
        <span className="nsl-tab-label">{tab.label}</span>

        {/* Badge de alertas */}
        {(badges[tab.id] ?? tab.badge) != null && (
          <span className="nsl-nav-badge" data-prueba={`badge-${tab.id}`}
            aria-label={`${badges[tab.id] ?? tab.badge} alertas`}>
            {badges[tab.id] ?? tab.badge}
          </span>
        )}
      </button>
    ))}
  </nav>
);

// ─────────────────────────────────────────────
// 🎛️ TAB PANEL — Renderiza la pestaña activa
// ─────────────────────────────────────────────
const TabPanel = ({ tab }) => {
  const TabComponent = tab.component;

  return (
    <div
      role="tabpanel"
      id={`nsl-panel-${tab.id}`}
      aria-labelledby={`nsl-tab-${tab.id}`}
      className="nsl-tab-panel"
    >
      {/* Encabezado interno de la pestaña */}
      <div className="nsl-page-topbar">
        <div>
          <h1 className="nsl-page-title">
            <span className="nsl-page-icon" aria-hidden="true">
              {tab.icon}
            </span>
            {tab.label}
          </h1>
          <p className="nsl-page-sub">{tab.description}</p>
        </div>
      </div>

      {/* Contenido — con Suspense + ErrorBoundary */}
      <TabErrorBoundary tabLabel={tab.label}>
        <Suspense fallback={<QuirofanoLoader tabLabel={tab.label} />}>
          <TabComponent />
        </Suspense>
      </TabErrorBoundary>
    </div>
  );
};

// ─────────────────────────────────────────────
// 🏗️ FINANCIAL DASHBOARD — Componente Raíz
// ─────────────────────────────────────────────
const FinancialDashboard = ({ dark }) => {
  // ── Estado de pestaña activa ──────────────────
  const [activeTab, setActiveTab] = useState(() => {
    // Persistir la última pestaña visitada en sessionStorage
    try {
      return sessionStorage.getItem('nsl_active_tab') || 'finanzas';
    } catch {
      return 'finanzas';
    }
  });

  // ── El tema viene del padre (NSLDashboard) ─────
  const theme = dark ? 'dark' : 'light';

  // ── Persistir la pestaña activa ───────────────
  useEffect(() => {
    try {
      sessionStorage.setItem('nsl_active_tab', activeTab);
    } catch {
      /* no-op */
    }
  }, [activeTab]);

  // ── Cambiar pestaña ───────────────────────────
  const handleTabChange = useCallback((tabId) => {
    setActiveTab(tabId);
    // Scroll al inicio del contenedor principal al cambiar pestaña
    const main = document.getElementById('nsl-main');
    if (main) main.scrollTop = 0;
  }, []);

  // ── EL CONTADOR DE ALERTAS DICE LA VERDAD (20-09-2026) ─────────────────
  // Las pestañas Alertas y Churn llevaban un «3» y un «2» escritos a mano:
  // salían siempre, hubiera lo que hubiera. Rob: «de lo que no me entero, no
  // lo sé». Un contador fijo es peor que ninguno, porque enseña a ignorarlo.
  // Ahora Alertas cuenta lo urgente de verdad (crítico + peligro) desde
  // cualquier pestaña, y desaparece cuando no hay nada. Pregunta al resumen
  // ligero por dominio, que sale de la caché de 60 s del radar, y calla con
  // la pestaña oculta para no gastar transferencia de Neon.
  const [badges, setBadges] = useState({});
  const ultimoBadge = useRef(undefined);
  useLatido(async () => {
    const { data } = await apiClient.get('/api/admin/alerts/domains');
    const urgentes = Object.values(data || {})
      .reduce((s, d) => s + (Number(d?.urgent) || 0), 0);
    const nuevo = urgentes > 0 ? urgentes : null;
    // Se compara contra un ref y no dentro de `setBadges`: React puede aplazar
    // esa función, y el latido necesita saber YA si hubo novedad.
    const cambio = ultimoBadge.current !== nuevo;
    ultimoBadge.current = nuevo;
    if (cambio) setBadges((prev) => ({ ...prev, alertas: nuevo }));
    return cambio;
  }, { base: 120000, techo: 600000 });

  // ── [P-08 FIX] Navegación programática entre tabs (deep-link inter-módulo) ─
  // Escucha el evento "nsl:goto-tab" emitido por cualquier tab hijo.
  // Permite que AlumnosTab navegue al Chat Hub sin prop-drilling.
  useEffect(() => {
    const handler = (e) => {
      const tabId = e?.detail?.tabId;
      if (tabId) handleTabChange(tabId);
    };
    window.addEventListener('nsl:goto-tab', handler);
    return () => window.removeEventListener('nsl:goto-tab', handler);
  }, [handleTabChange]);

  // ── Resolver la pestaña activa ────────────────
  const activeTabDef = TABS.find((t) => t.id === activeTab) || TABS.find((t) => t.id === 'finanzas') || TABS[0];

  // ────────────────────────────────────────────
  return (
    <div
      className="nsl-hub"
      data-theme={theme}
      data-active-tab={activeTab}
    >
      {/* ── 1. TOPBAR ── */}
      <Topbar theme={theme} />

      {/* ── 2. NAVBAR (16 pestañas) ── */}
      <Navbar activeTab={activeTab} onTabChange={handleTabChange} badges={badges} />

      {/* ── 3. ÁREA DE CONTENIDO PRINCIPAL ── */}
      <main
        id="nsl-main"
        className="nsl-main"
        role="main"
        aria-live="polite"
      >
        {/*
         * Renderiza solo la pestaña activa.
         * Se usa una key para forzar re-mount al cambiar
         * y garantizar animación de entrada.
         */}
        <TabPanel key={activeTab} tab={activeTabDef} />
      </main>

      {/* ── 4. PORTAL DE MODALES (slot global) ── */}
      <div id="nsl-modal-root" aria-live="assertive" />

      {/* ── 5. PORTAL DE TOASTS ── */}
      <div id="nsl-toast-root" aria-live="polite" aria-atomic="true" />
    </div>
  );
};

export default FinancialDashboard;